import * as d3 from 'd3';
import React from "react";
import styled from 'styled-components';

import ChartDimensions from '../../models/ChartDimensions';
import ChartMargins from '../../models/ChartMargins';
import ChartPaddings from '../../models/ChartPaddings';

import HorizontalAxis from './HorizontalAxis';
import Scalable from '../../interfaces/Scalable';
import VerticalAxis from './VerticalAxis';
import HerfindahlRecord from '../../models/HerfindahlRecord';
import Branch from '../../interfaces/Branch';


const DIMENSIONS: ChartDimensions = {
  width: 720,
  height: 360,
};

const MARGINS: ChartMargins = {
  top: 10,
  left: 70,
  bottom: 60,
  right: 10,
};

const PADDINGS: ChartPaddings = {
  left: 30,
  right: 30,
};

const LINE_STROKE = {
  'TOTAL': '#00A7FF',
  'VIDA': '#00D54A',
  'NO VIDA': '#F44E3F',
};

interface Props {
  series: HerfindahlRecord[],
  customStyle?: object
}

interface LinesProps extends Scalable {
  series: HerfindahlRecord[],
}

interface LinePathProps {
  adjusted: boolean
}

interface Series {
  name: Branch,
  adjusted: boolean,
  values: HerfindahlRecord[]
}

const StyledHerfindahl = styled.svg`
  & line {
    stroke: #FBFBFB;
  }

  & text {
    stroke: none;
    fill: #FBFBFB;
  }

  & .horizontal-axis {
    font-family: DMSans-Bold;
  }
`;

const HerfindalLine = styled.path`
  fill: none;
  stroke: ${ props => props.stroke };
  stroke-width: 5px;
  stroke-linejoin: miter;

  ${ (props: LinePathProps) => props.adjusted ? 'stroke-dasharray: 5 5' : '' }
`;

const Lines: React.FC<LinesProps> = (props: LinesProps) => {
  const series = props.series;

  const placeholder: { [key: string]: Series } = {};
  const values = series
    .sort((a, b) => a.year - b.year)
    .reduce((result, item) => {
      const key = `${item.branch}_${item.adjusted}`;

      if (!result[key]) {
        result[key] = { name: item.branch, adjusted: item.adjusted, values: [] };
      }

      result[key].values.push(item);

      return result;
    }, placeholder);

  return (
    <g>
      {
        Object.entries(values).map(entry => {
          const series: Series = entry[1];
          const line = d3.line()
            // @ts-ignore
            .x(d => props.xScale(d.year))
            // @ts-ignore
            .y(d => props.yScale(d.value));

          // @ts-ignore
          const herfindalLine = line(series.values);

          return (
            <HerfindalLine
                d={ herfindalLine }
                // @ts-ignore
                stroke={ LINE_STROKE[series.name] }
                adjusted={ series.adjusted }
                key={ entry[0] }
              />
          );
        })
      }
    </g>
  )
};


const HerfindalChart: React.FC<Props> = (props: Props) => {
  const series = props.series;
  const years = Array.from(new Set(series.map(x => x.year)))

  const xScale = d3.scaleLinear()
    // @ts-ignore
    .domain(d3.extent(years))
    .range([MARGINS.left + PADDINGS.left, MARGINS.left + DIMENSIONS.width - PADDINGS.right]);

  const yScale = d3.scaleLinear()
    // @ts-ignore
    .domain(d3.extent(series.map(x => x.value)))
    .range([MARGINS.top + DIMENSIONS.height, MARGINS.top]);

  const xTicks = Array.from(years);
  const yTicks = yScale.nice().ticks(4);

  const customStyle = props.customStyle || {};

  return (
    <StyledHerfindahl
      viewBox={`0 0 ${DIMENSIONS.width + MARGINS.left + MARGINS.right} ${DIMENSIONS.height + MARGINS.top + MARGINS.bottom}`}
      preserveAspectRatio='xMidYMin meet'
      style={{...customStyle}}
    >
      <VerticalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ yTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
        />
      <HorizontalAxis
          xScale={ xScale }
          yScale={ yScale }
          ticks={ xTicks }
          dimensions={ DIMENSIONS }
          margins={ MARGINS }
          paddings={ PADDINGS }
          formatTicks={ (tick: number) => (tick.toString()) }
        />
      <Lines xScale={ xScale } yScale={ yScale } series={ series } />
    </StyledHerfindahl>
  )
}

export default HerfindalChart;
